import { Button, Form, Input, Select, Spin, Table, TableColumnsType } from 'antd'
import type { TablePaginationConfig } from 'antd/es/table/interface'
import { SearchStakingErc3643TransactionsParams } from 'Modules'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { stakingErc3643Api } from 'api/staking-erc3643-api'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ButtonCopy from 'component/button/button-copy'
import ButtonExport from 'component/button/button-export'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { STAKING_ERC3643_TX_TO_API } from 'constants/staking-erc3643'
import { mapPoolOverview, mapPoolToRow, mapTransactionRow } from 'extends/staking-erc3643.extend'
import useSearchParams from 'hooks/use-search-params'
import { convertDateToString } from 'utils/format-time'
import { logError } from 'utils/logger'

import PoolDetailOverview from './components/pool-detail-overview'
import { PoolDetailStats, PoolTransactionRow, PoolTransactionType, StakingPoolRow } from './types'

import './style.scss'

const TX_TYPES: Array<PoolTransactionType | 'All types'> = [
  'All types',
  'Stake',
  'Unstake',
  'Claim rewards',
]

const renderTxType = (type: PoolTransactionType) => {
  if (type === 'Stake') {
    return <span className="status-pending">{type}</span>
  }
  if (type === 'Unstake') {
    return <span className="status-fail">{type}</span>
  }
  return <span className="status-success">{type}</span>
}

const PoolDetailPage = () => {
  const { poolId } = useParams()
  const [filterForm] = Form.useForm()
  const { searchParams, setSearchParams, initPagination, onChangePagination } =
    useSearchParams<SearchStakingErc3643TransactionsParams>({
      sort: 'timestamp:desc',
    })

  const [pool, setPool] = useState<StakingPoolRow | null>(null)
  const [detailStats, setDetailStats] = useState<PoolDetailStats | null>(null)
  const [transactions, setTransactions] = useState<PoolTransactionRow[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [totalTx, setTotalTx] = useState(0)
  const [loadingPool, setLoadingPool] = useState(true)
  const [loadingTx, setLoadingTx] = useState(false)

  const loadPool = useCallback(async () => {
    if (!poolId) return
    setLoadingPool(true)
    try {
      const res = await stakingErc3643Api.getPoolDetail(poolId)
      if (res.data?.success && res.data.data) {
        const apiPool = res.data.data
        setPool(mapPoolToRow(apiPool))
        setDetailStats(mapPoolOverview(apiPool, apiPool.overview))
      } else {
        setPool(null)
        setDetailStats(null)
      }
    } catch (error) {
      logError('Load pool detail failed', error)
      setPool(null)
      setDetailStats(null)
    } finally {
      setLoadingPool(false)
    }
  }, [poolId])

  const loadTransactions = useCallback(async () => {
    if (!poolId) return
    setLoadingTx(true)
    try {
      const res = await stakingErc3643Api.getPoolTransactions(poolId, searchParams)
      if (res.data?.success && res.data.data) {
        setTransactions((res.data.data.results ?? []).map(mapTransactionRow))
        setTotalResults(res.data.data.totalResults ?? 0)
        setTotalTx(res.data.data.total ?? 0)
      }
    } catch (error) {
      logError('Load pool transactions failed', error)
      setTransactions([])
    } finally {
      setLoadingTx(false)
    }
  }, [poolId, searchParams])

  useEffect(() => {
    loadPool()
  }, [loadPool])

  useEffect(() => {
    loadTransactions()
  }, [loadTransactions])

  const columns: TableColumnsType<PoolTransactionRow> = [
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      render: (text) => (
        <div className="staking-3643-address-cell">
          <LinkAddress address={text} type="address" head={6} tail={8} />
          {text ? <ButtonCopy text={text} /> : null}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: PoolTransactionType) => renderTxType(type),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, record) => (
        <strong>
          {new Intl.NumberFormat('en-US').format(record.amount)} {record.symbol}
        </strong>
      ),
    },
    {
      title: 'Tx Hash',
      dataIndex: 'txHash',
      key: 'txHash',
      render: (text) => <LinkAddress address={text} type="tx" />,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => <span>{convertDateToString(value)}</span>,
    },
  ]

  const onTableChange = (pagination: TablePaginationConfig) => {
    onChangePagination(pagination)
    setSearchParams((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }))
  }

  if (loadingPool) {
    return (
      <div className="main-section staking staking-erc-3643">
        <Spin />
      </div>
    )
  }

  if (!pool) {
    return (
      <div className="main-section staking staking-erc-3643">
        <span>Pool not found.</span>
      </div>
    )
  }

  return (
    <div className="main-section staking staking-erc-3643">
      <Breadcrumb
        additionalItems={[BREADCRUMBS.STAKING_ERC3643, BREADCRUMBS.STAKING_ERC3643_POOL_DETAIL]}
      />

      <div className="staking-3643-pool-detail-header">
        <div className="pool-symbol">{pool.ptoSymbol}</div>
        <div className="pool-name">{pool.ptoName}</div>
      </div>

      {detailStats ? <PoolDetailOverview data={detailStats} /> : null}

      <div className="table-row">
        <div className="main-table-row">
          <div>
            <Form
              form={filterForm}
              className="staking-3643-filter-form"
              layout="inline"
              onFinish={(values) => {
                const type =
                  values.type && values.type !== 'All types'
                    ? STAKING_ERC3643_TX_TO_API[values.type as PoolTransactionType]
                    : undefined
                setSearchParams({
                  page: 1,
                  limit: DEFAULT_PAGE_SIZE,
                  keyword: (values.wallet ?? '').trim() || undefined,
                  type,
                  sort: 'timestamp:desc',
                })
              }}
              style={{ marginTop: 10, marginBottom: 20 }}
              initialValues={{ type: 'All types' }}
            >
              <Form.Item name="wallet">
                <Input allowClear placeholder="Enter wallet address" style={{ minWidth: 260 }} />
              </Form.Item>
              <Form.Item name="type">
                <Select
                  style={{ minWidth: 180 }}
                  options={TX_TYPES.map((t) => ({ value: t, label: t }))}
                  placeholder="Choose type"
                />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
              </Form.Item>
              <Form.Item>
                <Button
                  onClick={() => {
                    filterForm.resetFields()
                    setSearchParams({ page: 1, limit: DEFAULT_PAGE_SIZE, sort: 'timestamp:desc' })
                  }}
                >
                  Reset
                </Button>
              </Form.Item>
              <Form.Item>
                <ButtonExport
                  fileType="staking_erc3643_transactions"
                  onExport={() =>
                    stakingErc3643Api.exportPoolTransactions(poolId as string, {
                      keyword: searchParams.keyword,
                      type: searchParams.type,
                      sort: searchParams.sort,
                    })
                  }
                />
              </Form.Item>
              <Form.Item className="staking-3643-filter-count">
                <span className="main-color-table">
                  {totalResults} of {totalTx} transactions
                </span>
              </Form.Item>
            </Form>
          </div>

          <Table<PoolTransactionRow>
            rowKey="id"
            columns={columns}
            dataSource={transactions}
            loading={loadingTx}
            className="main-table"
            scroll={{ x: 1000 }}
            pagination={{
              ...initPagination(totalResults),
              pageSizeOptions: DEFAULT_PAGE_OPTIONS,
              showSizeChanger: totalResults > 10,
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} transactions`,
            }}
            onChange={onTableChange}
          />
        </div>
      </div>
    </div>
  )
}

export default PoolDetailPage
